import type { BillingInfoPayload } from '@/modules/Billing/types'

const SECONDS_IN_DAY = 3600 * 24
export const resolveTrialDaysLeft = (billingInfo: BillingInfoPayload): number => {
  const { customer } = billingInfo
  const now = Math.floor(Date.now() / 1000)
  const trialEnd = customer.stripe_subscription_period_end || 0
  return Math.max(Math.round((trialEnd - now) / SECONDS_IN_DAY), 0)
}

type SubscriptionPlanName = 'freemium' | 'essentials' | 'team' | 'business' | 'enterprise'

export enum PlanPricing {
  freemium = 100,
  team = 300,
  business = 700,
  enterprise = 3500,
}

export const resolvePlanForCredit = (
  credit: number,
): Exclude<SubscriptionPlanName, 'essentials'> => {
  if (credit <= PlanPricing.freemium) {
    return 'freemium'
  }
  if (credit <= PlanPricing.team) {
    return 'team'
  }
  if (credit < PlanPricing.enterprise) {
    return 'business'
  }
  return 'enterprise'
}

import type { CustomerPayload, CustomerSubscriptionPayload } from './types'

type PartialBillingInfo = {
  customer: Pick<
    CustomerPayload,
    'stripe_subscription_period_end' | 'stripe_subscription_period_start'
  >

  customer_subscription: Pick<
    CustomerSubscriptionPayload,
    | 'annotation_credits_bonus'
    | 'annotation_credits_standard_max_in_period_at'
    | 'annotation_credits_standard_max_in_period'
    | 'annotation_credits_standard'
    | 'annotation_credits_used'
    | 'storage_standard_max_in_period_at'
    | 'storage_standard_max_in_period'
    | 'storage_standard'
    | 'storage_used'
  > | null
}

/**
 * Returns how many credits the customer will be charged for when the next
 * period starts. This can be less than the amount they were charged for in the
 * current period, since they might have reduced their subscription.
 */
export const annotationCreditsBilledNextPeriod = (billingInfo: PartialBillingInfo): number => {
  if (!billingInfo.customer_subscription) {
    throw new Error('No subscription record for customer')
  }
  const { annotation_credits_standard: standard } = billingInfo.customer_subscription
  return standard
}
